import { useEffect } from 'react';

import Loading from '../../shared/Loading';
import { Modal, SpeechBubble } from '../../shared';

import { useCarouselFeaturedPacks } from '../../hooks/carousels';
import { usePacksList } from '../../hooks/packs';

import { handleError } from '../../utils/utils';

import { PackListItem } from '../../types/packTypes';

import styles from './FeaturedPacks.module.scss';

type FeaturedPacksProps = {
    onLoad: (packItems: PackListItem[]) => void;
};

export const FeaturedPacks = (props: FeaturedPacksProps) => {
    const {
        packs,
        isLoading: isPacksLoading,
        error: packsError,
    } = usePacksList({ enabled: true });
    const {
        featuredPacks,
        fetch: fetchFeaturedPacks,
        isPending: isFeaturedPacksLoading,
        error: featuredPacksError,
    } = useCarouselFeaturedPacks();

    function getFeaturedPacksLoadingText() {
        if (isFeaturedPacksLoading) {
            return 'Loading featured packs from google...';
        }

        if (featuredPacksError) {
            return `Couldn't load featured packs. Server says: ${featuredPacksError.toString()}`;
        }

        return '';
    }

    async function applyCarouselImportedContent() {
        if (!featuredPacks?.length || !packs?.length) {
            return;
        }

        const { packItems, missingPacks } = featuredPacks.reduce(
            (
                acc: { packItems: PackListItem[]; missingPacks: string[] },
                packId,
            ) => {
                const packItem = packs.find((pack) => pack.packId === packId);

                if (packItem) {
                    acc.packItems.push(packItem);
                } else {
                    acc.missingPacks.push(packId);
                }

                return acc;
            },
            { packItems: [], missingPacks: [] },
        );

        if (missingPacks.length) {
            const isConfirmed = await Modal.confirm({
                heading: 'Some packs are missing in CMS',
                text: missingPacks.join('\n'),
                okLabel: 'Skip missing',
                cancelLabel: 'Cancel import',
            });

            if (!isConfirmed) return;
        }

        props.onLoad(packItems);
    }

    useEffect(() => {
        applyCarouselImportedContent();
    }, [featuredPacks, packs]);

    if (isPacksLoading) {
        return <Loading style={{ textAlign: 'center' }} />;
    }

    if (packsError) {
        return (
            <div className={styles.errorMessage}>
                {handleError(
                    'Unable to initialise featured packs import: ',
                    packsError,
                    false,
                )}
            </div>
        );
    }

    const featuredPacksLoadingText = getFeaturedPacksLoadingText();

    return (
        <div className={styles.importWrapper}>
            <button
                disabled={isFeaturedPacksLoading}
                onClick={() => fetchFeaturedPacks()}
            >
                Load popular packs from Google Sheet
            </button>

            {featuredPacksLoadingText && (
                <SpeechBubble
                    style={{
                        justifyContent: 'left',
                        marginLeft: 20,
                    }}
                >
                    {featuredPacksLoadingText}
                </SpeechBubble>
            )}
        </div>
    );
};
